// Some widget configs used to store colors as { hex: '#rrggbb' } (the shape react-color's
// SketchPicker returns). Persisted dashboards may still have that shape on disk even after the
// field's declared type moved to a plain hex string - this normalizes either shape to a string.
/**
 * @param {string | { hex: string }} value
 * @returns {string}
 */
export function normalizeColorHex(value) {
	if (value && typeof value === 'object') return value.hex;
	return value;
}

export function gradient(startColor, endColor, n, mode = 'hsl', includeEnds = true) {
	const a = parseColor(startColor);
	const b = parseColor(endColor);

	if (!a || !b) throw new Error('Unsupported color format. Use hex like #rrggbb or rgb(a).');

	const colors = [];
	const steps = Math.max(2, n);
	const count = includeEnds ? steps : steps + 2;

	for (let i = 0; i < count; i++) {
		const t = i / (count - 1);
		let rgb;
		if (mode.toLowerCase() === 'rgb') {
			rgb = lerpRGB(a.rgb, b.rgb, t);
		} else {
			// HSL is easest to interpolate
			rgb = lerpHSL(a.rgb, b.rgb, t);
		}
		colors.push(rgbToHex(rgb));
	}

	return includeEnds ? colors : colors.slice(1, colors.length - 1);
}

export function parseColor(str) {
	str = String(str).trim().toLowerCase();
	if (str.startsWith('#')) {
		return { rgb: hexToRgb(str) };
	}
	if (str.startsWith('rgb')) {
		return { rgb: rgbStringToRgb(str) };
	}
	return null;
}

export function hexToRgb(hex) {
	let h = hex.replace('#', '');
	if (h.length === 3)
		h = h
			.split('')
			.map((c) => c + c)
			.join('');
	const num = parseInt(h, 16);
	return {
		r: (num >> 16) & 255,
		g: (num >> 8) & 255,
		b: num & 255,
	};
}

export function rgbToHex({ r, g, b }) {
	const toHex = (v) => v.toString(16).padStart(2, '0');
	return (
		'#' +
		toHex(clamp(Math.round(r), 0, 255)) +
		toHex(clamp(Math.round(g), 0, 255)) +
		toHex(clamp(Math.round(b), 0, 255))
	);
}

export const rgbaToHex = (r, g, b, a) => {
	const toHex = (v) => (v || v === 0 ? Math.round(v).toString(16).padStart(2, '0') : '');
	const alpha = a || a === 0 ? Math.round(a * 255) : '';
	return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(alpha)}`;
};

const rgbaStringToHex = (rgba) => {
	const values = rgba.match(/[\d.]+/g).map(Number);
	const [r, g, b, a = 1] = values;
	const toHex = (v) => Math.round(v).toString(16).padStart(2, '0');
	const alpha = Math.round(a * 255);
	return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(alpha)}`;
};

export const hexToRgba = (hex) => {
	hex = hex.replace(/^#/, '');

	let r,
		g,
		b,
		a = 1;

	if (hex.length === 8) {
		r = parseInt(hex.substring(0, 2), 16);
		g = parseInt(hex.substring(2, 4), 16);
		b = parseInt(hex.substring(4, 6), 16);
		a = parseInt(hex.substring(6, 8), 16) / 255;
	} else if (hex.length === 6) {
		r = parseInt(hex.substring(0, 2), 16);
		g = parseInt(hex.substring(2, 4), 16);
		b = parseInt(hex.substring(4, 6), 16);
	} else if (hex.length === 4) {
		r = parseInt(hex[0] + hex[0], 16);
		g = parseInt(hex[1] + hex[1], 16);
		b = parseInt(hex[2] + hex[2], 16);
		a = parseInt(hex[3] + hex[3], 16) / 255;
	} else if (hex.length === 3) {
		r = parseInt(hex[0] + hex[0], 16);
		g = parseInt(hex[1] + hex[1], 16);
		b = parseInt(hex[2] + hex[2], 16);
	}

	return { r, g, b, a };
};

export const hexToRgbaString = (hex) => {
	const { r, g, b, a } = hexToRgba(hex);
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export function rgbStringToRgb(s) {
	const nums = s
		.replace(/rgba?\(/, '')
		.replace(')', '')
		.split(',')
		.map((x) => parseFloat(x.trim()));
	return { r: nums[0], g: nums[1], b: nums[2] };
}

export function clamp(v, min, max) {
	return Math.min(max, Math.max(min, v));
}

export function lerp(a, b, t) {
	return a + (b - a) * t;
}

export function lerpRGB(a, b, t) {
	return {
		r: lerp(a.r, b.r, t),
		g: lerp(a.g, b.g, t),
		b: lerp(a.b, b.b, t),
	};
}

export function lerpHSL(aRgb, bRgb, t) {
	const a = rgbToHsl(aRgb);
	const b = rgbToHsl(bRgb);

	let h1 = a.h,
		h2 = b.h;
	const dh = ((((h2 - h1) % 360) + 540) % 360) - 180;
	const h = (h1 + dh * t + 360) % 360;

	const s = lerp(a.s, b.s, t);
	const l = lerp(a.l, b.l, t);

	return hslToRgb({ h, s, l });
}

export function rgbToHsl({ r, g, b }) {
	r /= 255;
	g /= 255;
	b /= 255;
	const max = Math.max(r, g, b),
		min = Math.min(r, g, b);
	let h,
		s,
		l = (max + min) / 2;

	if (max === min) {
		h = s = 0;
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h = h * 60;
	}
	return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }) {
	h = ((h % 360) + 360) % 360;
	s /= 100;
	l /= 100;

	if (s === 0) {
		const v = Math.round(l * 255);
		return { r: v, g: v, b: v };
	}

	const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
	const p = 2 * l - q;

	const hk = h / 360;
	const r = hue2rgb(p, q, hk + 1 / 3);
	const g = hue2rgb(p, q, hk);
	const b = hue2rgb(p, q, hk - 1 / 3);

	return {
		r: Math.round(r * 255),
		g: Math.round(g * 255),
		b: Math.round(b * 255),
	};
}

export function hue2rgb(p, q, t) {
	if (t < 0) t += 1;
	if (t > 1) t -= 1;
	if (t < 1 / 6) return p + (q - p) * 6 * t;
	if (t < 1 / 2) return q;
	if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
	return p;
}
